import { Component, EventEmitter, HostListener, Input, OnDestroy, Output, inject } from '@angular/core';
import { NgxExtendedPdfViewerModule, PDFScriptLoaderService } from 'ngx-extended-pdf-viewer';
import { TranslocoPipe } from '@jsverse/transloco';
import { TranslationLanguage, translationLanguages } from '../../core/models/api.models';
import { normalizePdfFileName } from './pdf-file-name';

@Component({
  selector: 'app-pdf-preview', standalone: true,
  imports: [NgxExtendedPdfViewerModule, TranslocoPipe],
  styles: [`
    .settings-control option {
      background-color: #fff;
      color: #0f172a;
    }
    .settings-control option:hover {
      background-color: #f1f5f9;
    }
    .settings-control option:checked {
      background-color: #dbeafe;
      color: #1e3a8a;
    }
    .settings-control option:disabled {
      background-color: #f8fafc;
      color: #475569;
    }
  `],
  template: `<div class="fixed inset-0 z-[80] grid place-items-center bg-slate-950/70 p-3 backdrop-blur-sm sm:p-8" role="dialog" aria-modal="true">
    <section class="flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl dark:bg-slate-900">
      <header class="flex min-h-14 shrink-0 flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2 sm:px-5 dark:border-slate-700">
        <h2 class="min-w-0 flex-1 truncate text-sm font-semibold">{{ title }}</h2>
        <div class="flex shrink-0 items-center gap-2">
          @if (canTranslate) {
            <select #languageSelect class="settings-control disabled:opacity-50" style="width: auto; margin-top: 0"
              [attr.aria-label]="'translateDocument' | transloco" [disabled]="translating"
              (change)="selectLanguage(languageSelect)">
              <option value="" disabled selected>{{ (translating ? 'translatingDocument' : 'translateDocument') | transloco }}</option>
              @for (language of languages; track language.value) { <option [value]="language.value">{{ language.label }}</option> }
            </select>
          }
          <button type="button" class="rounded-lg px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800" (click)="close.emit()">{{ 'close' | transloco }} ×</button>
        </div>
      </header>
      @if (translating) { <p role="status" class="px-5 py-2 text-sm text-slate-500">{{ 'translatingDocument' | transloco }}</p> }
      @if (translationError) { <p role="alert" class="mx-3 my-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/80 dark:text-red-200">{{ translationError }}</p> }
      <div class="min-h-0 flex-1"><ngx-extended-pdf-viewer [src]="url" [filenameForDownload]="downloadFileName" (pdfLoaded)="onPdfLoaded()" [height]="'100%'" [showOpenFileButton]="false" /></div>
    </section>
  </div>`,
})
export class PdfPreview implements OnDestroy {
  private readonly pdfScriptLoader = inject(PDFScriptLoaderService);
  private restoreDownload?: () => void;
  @Input({ required: true }) url = '';
  @Input() title = 'Preview PDF';
  @Input() set displayName(value: string | null | undefined) {
    this.downloadFileName = normalizePdfFileName(value);
  }
  protected downloadFileName = 'document.pdf';
  @Input() canTranslate = false;
  @Input() translating = false;
  @Input() translationError: string | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() translate = new EventEmitter<TranslationLanguage>();
  protected readonly languages = translationLanguages;

  onPdfLoaded(): void {
    this.restoreDownload?.();
    this.restoreDownload = undefined;
    if (this.downloadFileName !== 'document.pdf') return;

    // ngx-extended-pdf-viewer 29.0.1 treats literal "document.pdf" as an
    // unspecified filename and derives it from the URL. Correct only this case
    // at the native download/save boundary, preserving PDF edits and toolbar.
    const manager = this.pdfScriptLoader.PDFViewerApplication.downloadManager;
    if (!manager) return;
    const download = manager.download;
    const correctedDownload: typeof download = (data, url) =>
      download.call(manager, data, url, this.downloadFileName);
    manager.download = correctedDownload;
    this.restoreDownload = () => {
      if (manager.download === correctedDownload) manager.download = download;
    };
  }

  ngOnDestroy(): void { this.restoreDownload?.(); }

  protected selectLanguage(select: HTMLSelectElement): void {
    const language = this.languages.find(item => item.value === select.value);
    select.value = '';
    if (language && this.canTranslate && !this.translating) this.translate.emit(language.value);
  }

  @HostListener('document:keydown.escape') escape(): void { this.close.emit(); }
}
